import { useEffect, useState } from 'react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet.jsx';
import { Button } from '@/components/ui/button.jsx';
import { Input } from '@/components/ui/input.jsx';
import { toast } from '@/components/ui/toaster.jsx';
import { Loader2, Plus, Trash2 } from 'lucide-react';
import { logHangarQuote, attachQuoteSourcePdf, getSnklSuggestion } from '@/services/quotes.service.js';
import TermPicker, { termMonthsOf } from '@/components/TermPicker.jsx';
import { presentSaveNotices } from '@/lib/quoteCompleteness.js';

// COVERAGE-LINE-ENVELOPE-SPEC §7 Slice 5 — broker "Log hangar quote" form.
// Builds the canonical coverage-line envelope (property + optional/included
// premises liability with a nested airport additional-insured) and POSTs it to
// /api/tracon/quotes/log-hangar. Application-driven: the broker sets what's
// included vs optional here; the client doesn't elect. "Make visible to client
// now" sets client_visible + present (the backend flips the deal to Presented).
//
// Mirrors HangarTermsSheet structure (shadcn Sheet, toast, onSaved). Lives in
// the hangar cockpit (SubmissionPanel), so it's inherently hangar-only.

function num(v) {
  if (v === '' || v === null || v === undefined) return null;
  const n = Number(String(v).replace(/[$,\s]/g, ''));
  return Number.isFinite(n) ? n : null;
}

function emptyBuilding() {
  return { occupancy: '', value: '', deductible: '', premium: '', cause_of_loss: '' };
}

// Contents & other property coverages a carrier quotes beside the building
// schedule. Keys are the hangar application's COVERAGE_ROWS vocabulary
// (utils/hangar-app-pdf.js), stored on the property line as
// payload.coverages[key] = { limit, deductible, premium }. Business personal
// property used to be logged as "Building 2" (Winter 2026-09-11); a BPP is not
// a building. `building_value` and `flood` are deliberately absent: the
// schedule above and the flood fields below are their homes.
// Guard: tests/hangar-quote-contents-and-fees.test.js pins this list to the server's.
export const OTHER_COVERAGES = [
  ['business_personal_property', 'Business Personal Property'],
  ['personal_property_of_others', 'Personal Property of Others'],
  ['business_income', 'Business Income'],
  ['extra_expense', 'Extra Expense'],
  ['misc_tools', 'Miscellaneous Tools'],
  ['employee_tools', 'Employee Tools'],
  ['mobile_equipment', 'Mobile Equipment'],
];

function emptyCoverage() {
  return { key: 'business_personal_property', limit: '', deductible: '', premium: '' };
}

function seed() {
  return {
    carrier: '',
    effective_date: '',
    term_months: '12',
    buildings: [emptyBuilding()],
    coverages: [], // contents & other coverages (see OTHER_COVERAGES)
    property_premium: '',
    valuation: 'RC',
    coinsurance: '',
    wind_hail_deductible: '',
    flood_limit: '',
    flood_deductible: '',
    flood_premium: '',
    liab_mode: 'none', // none | included | optional
    liab_each_occurrence: '1000000',
    liab_aggregate: '2000000',
    liab_premium: '',
    ai_on: false,
    ai_premium: '',
    ai_scope: 'Airport Authority only',
    subject_to: '',
    // The carrier needs a signed SNKL at binding (quotes.snkl_at_bind): null until
    // the broker or the suggestion states it; the binding page signs it with the application.
    snkl_at_bind: null,
    snkl_reason: '',
    // Envelope-grain taxes and fees (quotes.estimated_taxes / admin_fee). Blank
    // means "not stated"; both are billed on top of premium, never inside it.
    estimated_taxes: '',
    admin_fee: '',
    source_pdf: null, // optional carrier source-quote PDF (gap A)
    make_visible: true,
    // Edit-in-place carries the CLIENT's persisted elections through a broker
    // edit (line replace would otherwise silently reset them). null = creating.
    liab_selected_prev: null,
    ai_selected_prev: null,
  };
}

// Inverse of buildLines: seed the form from an existing envelope quote
// (quote + lines from GET /quotes/:id/full) so Edit opens prefilled.
function seedFromQuote(q) {
  const s = seed();
  if (!q) return s;
  const str = (v) => (v == null ? '' : String(v));
  s.carrier = q.carrier || '';
  s.effective_date = q.effective_date || '';
  // The stored term, or nothing: an edit never shows a 12 nobody picked.
  s.term_months = str(q.term_months);
  s.subject_to = q.subject_to || '';
  s.snkl_at_bind = typeof q.snkl_at_bind === 'boolean' ? q.snkl_at_bind : null;
  s.estimated_taxes = str(q.estimated_taxes);
  s.admin_fee = str(q.admin_fee);
  s.make_visible = q.client_visible === true;
  const lines = Array.isArray(q.lines) ? q.lines : [];
  const prop = lines.find((l) => l.line_type === 'property');
  if (prop) {
    const p = prop.payload || {};
    s.property_premium = str(prop.premium);
    s.valuation = p.valuation || '';
    s.coinsurance = str(p.coinsurance);
    s.wind_hail_deductible = str(p.wind_hail_deductible);
    if (p.flood && typeof p.flood === 'object') {
      s.flood_limit = str(p.flood.value);
      s.flood_deductible = str(p.flood.deductible);
      s.flood_premium = str(p.flood.premium);
    }
    if (Array.isArray(p.buildings) && p.buildings.length > 0) {
      s.buildings = p.buildings.map((b) => ({
        occupancy: b.occupancy || '',
        value: str(b.value),
        deductible: str(b.deductible),
        premium: str(b.premium),
        cause_of_loss: b.cause_of_loss || '',
      }));
    }
    if (p.coverages && typeof p.coverages === 'object') {
      s.coverages = Object.entries(p.coverages)
        .filter(([key]) => OTHER_COVERAGES.some(([k]) => k === key))
        .map(([key, c]) => ({ key, limit: str(c?.limit), deductible: str(c?.deductible), premium: str(c?.premium) }));
    }
  }
  const prem = lines.find((l) => l.line_type === 'premises');
  if (prem) {
    s.liab_mode = prem.optional === true ? 'optional' : 'included';
    if (prem.limits?.each_occurrence != null) s.liab_each_occurrence = str(prem.limits.each_occurrence);
    if (prem.limits?.aggregate != null) s.liab_aggregate = str(prem.limits.aggregate);
    s.liab_premium = str(prem.premium);
    s.liab_selected_prev = prem.selected === true;
    const ai = prem.payload?.additional_insured;
    if (ai) {
      s.ai_on = true;
      s.ai_premium = str(ai.premium);
      if (ai.scope) s.ai_scope = ai.scope;
      s.ai_selected_prev = ai.selected === true;
    }
  }
  return s;
}

// quote: an existing envelope quote (with .lines) to EDIT in place — the sheet
// opens prefilled and saves via log-hangar's quote_id update path. Absent =
// log a new quote.
export default function LogHangarQuoteSheet({ dealId, open, onOpenChange, onSaved, quote = null }) {
  const [f, setF] = useState(seed);
  const [saving, setSaving] = useState(false);
  const editing = !!quote?.id;

  // Reset on each open so a cancel discards edits; edit mode seeds from the quote.
  useEffect(() => { if (open) setF(quote ? seedFromQuote(quote) : seed()); }, [open, quote]);
  // An unstated SNKL box takes the suggestion (last placement, then the carrier),
  // with its reason shown under the box; a stated one stays as the broker left it.
  useEffect(() => {
    if (!open || !dealId) return;
    let live = true;
    getSnklSuggestion(dealId).then((r) => {
      if (!live || !r) return;
      setF((s) => (s.snkl_at_bind === null ? { ...s, snkl_at_bind: r.checked === true, snkl_reason: r.reason || '' } : { ...s, snkl_reason: s.snkl_reason || '' }));
    }).catch(() => {});
    return () => { live = false; };
  }, [open, dealId]);

  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const setBldg = (i, k, v) => setF((s) => ({ ...s, buildings: s.buildings.map((b, j) => (j === i ? { ...b, [k]: v } : b)) }));
  const addBldg = () => setF((s) => ({ ...s, buildings: [...s.buildings, emptyBuilding()] }));
  const removeBldg = (i) => setF((s) => ({ ...s, buildings: s.buildings.filter((_, j) => j !== i) }));
  const setCov = (i, k, v) => setF((s) => ({ ...s, coverages: s.coverages.map((c, j) => (j === i ? { ...c, [k]: v } : c)) }));
  const addCov = () => setF((s) => ({ ...s, coverages: [...s.coverages, emptyCoverage()] }));
  const removeCov = (i) => setF((s) => ({ ...s, coverages: s.coverages.filter((_, j) => j !== i) }));

  const hasBuilding = f.buildings.some((b) => num(b.value) != null);
  const liabOk = f.liab_mode === 'none' || num(f.liab_premium) != null;
  // A typed term that is not a whole number of months is refused by the server
  // (statedTermMonths); a blank one is allowed and the present gate lists it.
  const termOk = f.term_months === '' || termMonthsOf(f.term_months) != null;
  const canSave = !!f.carrier.trim() && !!f.effective_date && num(f.property_premium) != null && hasBuilding && liabOk && termOk && !saving;

  // Derived premium preview (selected lines = property + included liability).
  const baseTotal = (num(f.property_premium) || 0) + (f.liab_mode === 'included' ? (num(f.liab_premium) || 0) : 0);
  // What the schedule's own rows sum to. Shown beside the property premium and
  // offered, never written: a derived figure quietly replacing an entered one
  // is how a policy premium stops matching the quote it came from.
  const scheduleSum = [...f.buildings, ...f.coverages].reduce((s, r) => s + (num(r.premium) || 0), 0);
  const scheduleDiffers = scheduleSum > 0 && scheduleSum !== (num(f.property_premium) || 0);
  const taxes = num(f.estimated_taxes) || 0;
  const fees = num(f.admin_fee) || 0;

  function buildLines() {
    const buildings = f.buildings
      .filter((b) => num(b.value) != null)
      .map((b, i) => {
        const row = { bldg: i + 1 };
        if (b.occupancy.trim()) row.occupancy = b.occupancy.trim();
        row.value = num(b.value);
        if (num(b.deductible) != null) row.deductible = num(b.deductible);
        if (num(b.premium) != null) row.premium = num(b.premium);
        if (b.cause_of_loss.trim()) row.cause_of_loss = b.cause_of_loss.trim();
        return row;
      });
    const propPayload = { buildings };
    // Contents & other coverages: only rows with a stated limit are a coverage.
    const coverages = {};
    for (const c of f.coverages) {
      if (!c.key || num(c.limit) == null) continue;
      const row = { limit: num(c.limit) };
      if (num(c.deductible) != null) row.deductible = num(c.deductible);
      if (num(c.premium) != null) row.premium = num(c.premium);
      coverages[c.key] = row;
    }
    if (Object.keys(coverages).length > 0) propPayload.coverages = coverages;
    if (f.valuation.trim()) propPayload.valuation = f.valuation.trim();
    if (f.coinsurance.trim()) propPayload.coinsurance = f.coinsurance.trim();
    if (num(f.wind_hail_deductible) != null) propPayload.wind_hail_deductible = num(f.wind_hail_deductible);
    if (num(f.flood_limit) != null || num(f.flood_deductible) != null || num(f.flood_premium) != null) {
      propPayload.flood = { value: num(f.flood_limit), deductible: num(f.flood_deductible), premium: num(f.flood_premium) };
    }

    const lines = [
      { line_type: 'property', subject_kind: 'location', optional: false, premium: num(f.property_premium), payload: propPayload },
    ];

    if (f.liab_mode !== 'none') {
      const limits = {};
      if (num(f.liab_each_occurrence) != null) limits.each_occurrence = num(f.liab_each_occurrence);
      if (num(f.liab_aggregate) != null) limits.aggregate = num(f.liab_aggregate);
      const payload = { use: 'Private Use only' };
      if (f.ai_on && num(f.ai_premium) != null) {
        payload.additional_insured = { available: true, premium: num(f.ai_premium), scope: f.ai_scope.trim() || 'Airport Authority only' };
        // Edit-in-place: keep the client's persisted AI election alive across
        // a broker edit (lines are REPLACED on save).
        if (f.ai_selected_prev != null) payload.additional_insured.selected = f.ai_selected_prev === true;
      }
      const premLine = {
        line_type: 'premises',
        subject_kind: null,
        optional: f.liab_mode === 'optional',
        premium: num(f.liab_premium),
        limits,
        payload,
      };
      // Same carry-through for the liability election itself (optional lines
      // default to unselected on insert; a client's earlier "yes" survives).
      if (f.liab_mode === 'optional' && f.liab_selected_prev != null) premLine.selected = f.liab_selected_prev === true;
      lines.push(premLine);
    }
    return lines;
  }

  async function handleSave() {
    if (!dealId || !canSave) return;
    setSaving(true);
    try {
      const res = await logHangarQuote({
        deal_id: dealId,
        ...(editing ? { quote_id: quote.id } : {}),
        carrier: f.carrier.trim(),
        effective_date: f.effective_date,
        // As picked (decision 14). Blank stays blank: the present gate lists
        // 'term', the server never invents one, and neither does this sheet.
        term_months: termMonthsOf(f.term_months),
        client_visible: f.make_visible,
        present: f.make_visible,
        subject_to: f.subject_to.trim() || null,
        // Stated once the box shows a value (the broker's or the suggestion he saved).
        ...(typeof f.snkl_at_bind === 'boolean' ? { snkl_at_bind: f.snkl_at_bind } : {}),
        // Both keys always: null clears a previously entered figure on edit.
        estimated_taxes: num(f.estimated_taxes),
        admin_fee: num(f.admin_fee),
        lines: buildLines(),
      });
      if (!res?.success) { toast.error(res?.error || 'Save failed'); return; }
      // Optional source-PDF attach (gap A) — non-fatal: the quote is already saved.
      let attached = false;
      if (f.source_pdf && res.quote_id) {
        try { await attachQuoteSourcePdf(dealId, res.quote_id, f.source_pdf); attached = true; }
        catch (e) { toast.error(`Quote saved, but source PDF attach failed: ${e.message}`); }
      }
      // What the client sees is what the SAVE ANSWER says (S5): a request to
      // show that the present gate refused saved the quote and showed nothing.
      const shown = res.client_visible === true;
      const { refused, stillShown } = presentSaveNotices(res);
      toast.success((editing
        ? 'Hangar quote updated'
        : (f.make_visible && shown ? 'Hangar quote logged + presented to client' : 'Hangar quote logged')) + (attached ? ' (source PDF attached)' : ''));
      if (refused) toast.error(refused, { duration: Infinity });
      if (stillShown) toast.info(stillShown);
      onSaved?.(res);
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.body?.error || err?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="flex w-full flex-col gap-0 overflow-y-auto sm:max-w-xl">
        <SheetHeader>
          <SheetTitle>{editing ? 'Edit hangar quote' : 'Log hangar quote'}</SheetTitle>
          <SheetDescription>
            {editing
              ? 'Update the carrier quote in place. The Tower card and packet follow on save.'
              : "Enter a carrier's hangar/property quote. It presents in the client's Tower and is bindable. Premiums are pre-tax."}
          </SheetDescription>
        </SheetHeader>

        <div className="flex-1 space-y-5 py-4">
          {/* Carrier + effective date + term */}
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="mb-1 block text-[11px] text-text-dim">Carrier *</span>
              <Input value={f.carrier} onChange={(e) => set('carrier', e.target.value)} placeholder="e.g. National Hangar Insurance Program" />
            </label>
            <label className="block">
              <span className="mb-1 block text-[11px] text-text-dim">Effective date *</span>
              <Input type="date" value={f.effective_date} onChange={(e) => set('effective_date', e.target.value)} />
            </label>
            {/* Decision 14: 12 pre-picked on a new log, 6 and Other beside it; an
                edit shows the stored term, and a stored blank shows nothing. */}
            <div className="col-span-2">
              <TermPicker value={f.term_months} onChange={(v) => set('term_months', v)} disabled={saving} label="Term" />
            </div>
          </div>

          {/* Building schedule */}
          <div>
            <div className="mb-1.5 flex items-center justify-between">
              <h3 className="text-[11px] font-semibold uppercase tracking-wide text-text-dim">Buildings</h3>
              <Button type="button" variant="ghost" size="sm" onClick={addBldg}><Plus className="mr-1 h-3.5 w-3.5" />Add building</Button>
            </div>
            <div className="space-y-2">
              {f.buildings.map((b, i) => (
                <div key={i} className="rounded-card border border-border p-3">
                  <div className="mb-2 flex items-center justify-between">
                    <span className="text-xs font-medium text-text">Building {i + 1}</span>
                    {f.buildings.length > 1 && (
                      <button type="button" onClick={() => removeBldg(i)} className="text-text-dim hover:text-red-500"><Trash2 className="h-3.5 w-3.5" /></button>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <label className="block"><span className="mb-1 block text-[11px] text-text-dim">Occupancy / name</span><Input value={b.occupancy} onChange={(e) => setBldg(i, 'occupancy', e.target.value)} placeholder="e.g. Hangar 4F" /></label>
                    <label className="block"><span className="mb-1 block text-[11px] text-text-dim">Value *</span><Input value={b.value} onChange={(e) => setBldg(i, 'value', e.target.value)} placeholder="$" /></label>
                    <label className="block"><span className="mb-1 block text-[11px] text-text-dim">Deductible</span><Input value={b.deductible} onChange={(e) => setBldg(i, 'deductible', e.target.value)} placeholder="$" /></label>
                    <label className="block"><span className="mb-1 block text-[11px] text-text-dim">Premium</span><Input value={b.premium} onChange={(e) => setBldg(i, 'premium', e.target.value)} placeholder="$" /></label>
                    <label className="col-span-2 block"><span className="mb-1 block text-[11px] text-text-dim">Cause of loss</span><Input value={b.cause_of_loss} onChange={(e) => setBldg(i, 'cause_of_loss', e.target.value)} placeholder="e.g. Special Including Theft" /></label>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Contents & other coverages (BPP, PPO, business income, ...) */}
          <div>
            <div className="mb-1.5 flex items-center justify-between">
              <h3 className="text-[11px] font-semibold uppercase tracking-wide text-text-dim">Contents &amp; other coverages</h3>
              <Button type="button" variant="ghost" size="sm" onClick={addCov}><Plus className="mr-1 h-3.5 w-3.5" />Add coverage</Button>
            </div>
            {f.coverages.length === 0 ? (
              <p className="text-[11px] text-text-dim">Business personal property, personal property of others, business income. Not buildings.</p>
            ) : (
              <div className="space-y-2">
                {f.coverages.map((c, i) => (
                  <div key={i} className="rounded-card border border-border p-3">
                    <div className="grid grid-cols-2 gap-2">
                      <label className="col-span-2 block"><span className="mb-1 block text-[11px] text-text-dim">Coverage</span>
                        <div className="flex items-center gap-2">
                          <select value={c.key} onChange={(e) => setCov(i, 'key', e.target.value)} className="h-10 w-full rounded-card border border-border bg-surface px-3 text-sm text-text focus:outline-none focus:ring-2 focus:ring-accent/40">
                            {OTHER_COVERAGES.map(([k, label]) => <option key={k} value={k}>{label}</option>)}
                          </select>
                          <button type="button" onClick={() => removeCov(i)} className="text-text-dim hover:text-red-500" aria-label="Remove coverage"><Trash2 className="h-3.5 w-3.5" /></button>
                        </div>
                      </label>
                      <label className="block"><span className="mb-1 block text-[11px] text-text-dim">Limit *</span><Input value={c.limit} onChange={(e) => setCov(i, 'limit', e.target.value)} placeholder="$" /></label>
                      <label className="block"><span className="mb-1 block text-[11px] text-text-dim">Deductible</span><Input value={c.deductible} onChange={(e) => setCov(i, 'deductible', e.target.value)} placeholder="$" /></label>
                      <label className="block"><span className="mb-1 block text-[11px] text-text-dim">Premium</span><Input value={c.premium} onChange={(e) => setCov(i, 'premium', e.target.value)} placeholder="$" /></label>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Property line terms + premium */}
          <div className="grid grid-cols-3 gap-3">
            <label className="block"><span className="mb-1 block text-[11px] text-text-dim">Property premium *</span><Input value={f.property_premium} onChange={(e) => set('property_premium', e.target.value)} placeholder="$" />
              {scheduleDiffers && (
                <span className="mt-1 block text-[11px] text-text-dim">
                  Schedule rows sum to ${scheduleSum.toLocaleString('en-US')}.{' '}
                  <button type="button" className="underline" onClick={() => set('property_premium', String(scheduleSum))}>Use it</button>
                </span>
              )}
            </label>
            <label className="block"><span className="mb-1 block text-[11px] text-text-dim">Valuation</span><Input value={f.valuation} onChange={(e) => set('valuation', e.target.value)} placeholder="RC / ACV" /></label>
            <label className="block"><span className="mb-1 block text-[11px] text-text-dim">Coinsurance</span><Input value={f.coinsurance} onChange={(e) => set('coinsurance', e.target.value)} placeholder="e.g. waived / 80%" /></label>
            <label className="block"><span className="mb-1 block text-[11px] text-text-dim">Wind / hail deductible</span><Input value={f.wind_hail_deductible} onChange={(e) => set('wind_hail_deductible', e.target.value)} placeholder="$" /></label>
            <label className="block"><span className="mb-1 block text-[11px] text-text-dim">Flood limit</span><Input value={f.flood_limit} onChange={(e) => set('flood_limit', e.target.value)} placeholder="$" /></label>
            <label className="block"><span className="mb-1 block text-[11px] text-text-dim">Flood deductible</span><Input value={f.flood_deductible} onChange={(e) => set('flood_deductible', e.target.value)} placeholder="$" /></label>
            <label className="block"><span className="mb-1 block text-[11px] text-text-dim">Flood premium</span><Input value={f.flood_premium} onChange={(e) => set('flood_premium', e.target.value)} placeholder="$0 if included" /></label>
          </div>

          {/* Liability */}
          <div>
            <h3 className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-text-dim">Liability</h3>
            <div className="mb-2 flex flex-wrap gap-2">
              {[['none', 'None'], ['included', 'Included'], ['optional', 'Optional add-on']].map(([id, label]) => (
                <Button key={id} type="button" variant={f.liab_mode === id ? 'default' : 'outline'} size="sm" onClick={() => set('liab_mode', id)}>{label}</Button>
              ))}
            </div>
            {f.liab_mode !== 'none' && (
              <div className="space-y-2 rounded-card border border-border p-3">
                <div className="grid grid-cols-3 gap-2">
                  <label className="block"><span className="mb-1 block text-[11px] text-text-dim">Each occurrence</span><Input value={f.liab_each_occurrence} onChange={(e) => set('liab_each_occurrence', e.target.value)} placeholder="$" /></label>
                  <label className="block"><span className="mb-1 block text-[11px] text-text-dim">Aggregate</span><Input value={f.liab_aggregate} onChange={(e) => set('liab_aggregate', e.target.value)} placeholder="$" /></label>
                  <label className="block"><span className="mb-1 block text-[11px] text-text-dim">Premium *</span><Input value={f.liab_premium} onChange={(e) => set('liab_premium', e.target.value)} placeholder="$" /></label>
                </div>
                <label className="flex items-center gap-2 text-sm text-text">
                  <input type="checkbox" checked={f.ai_on} onChange={(e) => set('ai_on', e.target.checked)} />
                  Airport additional insured
                </label>
                {f.ai_on && (
                  <div className="grid grid-cols-2 gap-2 pl-6">
                    <label className="block"><span className="mb-1 block text-[11px] text-text-dim">AI premium</span><Input value={f.ai_premium} onChange={(e) => set('ai_premium', e.target.value)} placeholder="$" /></label>
                    <label className="block"><span className="mb-1 block text-[11px] text-text-dim">AI scope</span><Input value={f.ai_scope} onChange={(e) => set('ai_scope', e.target.value)} /></label>
                  </div>
                )}
                <p className="text-[11px] text-text-dim">{f.liab_mode === 'included' ? 'Included in the base premium.' : 'Optional — the client sees it as an add-on; not in the base.'}</p>
              </div>
            )}
          </div>

          {/* Taxes & fees: billed on top of premium, never inside it. */}
          <div className="grid grid-cols-2 gap-3">
            <label className="block"><span className="mb-1 block text-[11px] text-text-dim">Estimated taxes / surplus lines</span><Input value={f.estimated_taxes} onChange={(e) => set('estimated_taxes', e.target.value)} placeholder="Leave blank when none apply" /></label>
            <label className="block"><span className="mb-1 block text-[11px] text-text-dim">Fees (policy, inspection, stamping)</span><Input value={f.admin_fee} onChange={(e) => set('admin_fee', e.target.value)} placeholder="Leave blank when none apply" /></label>
          </div>

          {/* The SNKL rides the bind: the client signs it with the application. */}
          <label className="flex items-start gap-2">
            <input type="checkbox" className="mt-0.5" checked={f.snkl_at_bind === true} onChange={(e) => setF((s) => ({ ...s, snkl_at_bind: e.target.checked, snkl_reason: '' }))} />
            <span className="text-xs text-text">
              Carrier needs a signed No Known Losses letter at binding (the client signs it with the application)
              {f.snkl_reason ? <span className="mt-0.5 block text-[11px] text-text-dim">{f.snkl_reason}</span> : null}
            </span>
          </label>

          {/* Subjectivities (broker-internal) */}
          <label className="block">
            <span className="mb-1 block text-[11px] text-text-dim">Subjectivities (broker-internal — not shown to client)</span>
            <Input value={f.subject_to} onChange={(e) => set('subject_to', e.target.value)} placeholder="e.g. signed SNKL; loss runs from incumbent" />
          </label>

          {/* Source quote PDF (optional, broker-internal) — attached to the quote */}
          <label className="block">
            <span className="mb-1 block text-[11px] text-text-dim">Source quote PDF (optional — the carrier&apos;s quote document)</span>
            <input
              type="file"
              accept="application/pdf"
              onChange={(e) => set('source_pdf', e.target.files?.[0] || null)}
              className="block w-full text-xs text-text-muted file:mr-2 file:rounded file:border file:border-border file:bg-surface file:px-2 file:py-1 file:text-xs file:text-text"
            />
            {f.source_pdf && <span className="mt-1 block text-[11px] text-text-dim">{f.source_pdf.name}</span>}
          </label>

          {/* Base premium preview + visibility */}
          <div className="rounded-card border border-border p-3">
            <div className="flex items-center justify-between text-sm">
              <span className="text-text-muted">Base premium (presented)</span>
              <span className="font-semibold text-text">${baseTotal.toLocaleString('en-US')}</span>
            </div>
            {taxes > 0 && <div className="flex items-center justify-between text-xs text-text-dim"><span>+ est. taxes</span><span>${taxes.toLocaleString('en-US')}</span></div>}
            {fees > 0 && <div className="flex items-center justify-between text-xs text-text-dim"><span>+ fees</span><span>${fees.toLocaleString('en-US')}</span></div>}
            {(taxes > 0 || fees > 0) && (
              <div className="mt-1 flex items-center justify-between border-t border-border pt-1 text-sm">
                <span className="text-text-muted">Client pays (before the platform fee)</span>
                <span className="font-semibold text-text">${(baseTotal + taxes + fees).toLocaleString('en-US')}</span>
              </div>
            )}
            <label className="mt-2 flex items-center gap-2 text-sm text-text">
              <input type="checkbox" checked={f.make_visible} onChange={(e) => set('make_visible', e.target.checked)} />
              Make visible to client now (moves the deal to Presented)
            </label>
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-border pt-3">
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
          <Button onClick={handleSave} disabled={!canSave}>
            {saving ? (<><Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />Saving…</>) : 'Log quote'}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
